/**
 * The tiled tech-icon pattern from the client's LinkedIn banner, rebuilt as a
 * coded SVG <pattern> rather than a cropped image — so it stays crisp at any
 * size, can drift and parallax, and recolours with the surface.
 *
 * Glyphs match the banner: terminal prompt, diagonal arrow, 2x2 grid, diamond,
 * and a crosshair/T-bar, each inside a rounded-square tile at ~7% white.
 */

interface TechPatternProps {
  className?: string;
  opacity?: number;
  /** Tile size in px. Reduced on small screens so glyphs stay readable. */
  size?: number;
}

export function TechPattern({ className = '', opacity = 0.07, size = 130 }: TechPatternProps) {
  // Unique ids: two TechPatterns on one page would otherwise share a <pattern>
  // definition, and the second would silently inherit the first's tile size.
  const uid = `${size}`;
  const idA = `falcon-tech-${uid}`;
  const idB = `falcon-tech-alt-${uid}`;

  return (
    <svg
      className={`pointer-events-none absolute inset-0 h-full w-full ${className}`}
      aria-hidden="true"
      focusable="false"
      style={{ opacity }}
    >
      <defs>
        <pattern id={idA} width={size} height={size} patternUnits="userSpaceOnUse">
          <g
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {/* tile frame */}
            <rect
              x={size * 0.14}
              y={size * 0.14}
              width={size * 0.72}
              height={size * 0.72}
              rx={size * 0.18}
              opacity="0.55"
            />
            {/* terminal prompt  >_ */}
            <g transform={`translate(${size * 0.34}, ${size * 0.4}) scale(${size / 180})`}>
              <path d="M0 0 L14 11 L0 22" />
              <path d="M20 24 L38 24" />
            </g>
          </g>
        </pattern>

        <pattern id={idB} width={size * 2} height={size * 2} patternUnits="userSpaceOnUse">
          <g
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {/* diagonal arrow, top-right tile */}
            <g transform={`translate(${size * 1.34}, ${size * 0.42}) scale(${size / 180})`}>
              <path d="M0 34 L34 0" />
              <path d="M12 0 L34 0 L34 22" />
            </g>
            {/* 2x2 grid, bottom-left tile */}
            <g transform={`translate(${size * 0.36}, ${size * 1.42}) scale(${size / 180})`}>
              <rect x="0" y="0" width="14" height="14" rx="2" />
              <rect x="20" y="0" width="14" height="14" rx="2" />
              <rect x="0" y="20" width="14" height="14" rx="2" />
              <rect x="20" y="20" width="14" height="14" rx="2" />
            </g>
            {/* diamond, bottom-right tile */}
            <g transform={`translate(${size * 1.42}, ${size * 1.42}) scale(${size / 180})`}>
              <path d="M16 0 L32 20 L16 40 L0 20 Z" />
            </g>
          </g>
        </pattern>
      </defs>

      <rect width="100%" height="100%" fill={`url(#${idA})`} />
      <rect width="100%" height="100%" fill={`url(#${idB})`} />
    </svg>
  );
}
